import { gsap } from "gsap"
import { useRef, useEffect } from "react"
import { motion } from "framer-motion"

// Add this after the imports
const scrollbarHideStyles = `
  /* Hide scrollbar for Chrome, Safari and Opera */
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbar for IE, Edge and Firefox */
  .scrollbar-hide {
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;  /* Firefox */
  }
`

export function TrustSignalsSection() {
  const statsRef = useRef<HTMLDivElement>(null)

  // Counter animation for stats
  useEffect(() => {
    const counters = document.querySelectorAll<HTMLElement>(".stat-counter");

    counters.forEach((el, i) => {
      const target = parseFloat(el.dataset.target ?? "0");
      const suffix = el.dataset.suffix ?? "";
      const decimals = el.dataset.target?.includes(".") ? 1 : 0;

      el.textContent = "0" + suffix;

      // 1. 创建一个 proxy 对象，让它存储当前要动画到的值
      const proxy = { value: 0 };

      gsap.to(proxy, {
        value: target,
        duration: 2,
        ease: "power2.out",
        // 2. onUpdate 时直接引用 proxy.value
        onUpdate: () => {
          const v = decimals
            ? proxy.value.toFixed(decimals)
            : Math.round(proxy.value);
          el.textContent = v + suffix;
        },
        onComplete: () => {
          el.textContent = target + suffix;
        },
      });
    });
  }, []);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1.0], // Apple's custom easing
      },
    },
  }

  // Stats data
  const stats = [
    {
      title: "Reliability",
      value: "99.9",
      suffix: "%",
      description: "Uptime SLA for critical services",
      icon: <img src="https://assets.avepoint.com/upload/images/landingpage/pricing/APS-Pricing-icons-reliability.svg" className="h-4 w-4 md:h-6 md:w-6 text-[#ef483d]" alt="Reliability" />,
      iconBg: "bg-[#e8f5e9]",
      blobColor: "bg-[#25c5a1]",
    },
    {
      title: "Users",
      value: "2",
      suffix: "M+",
      description: "Public sector users protected",
      icon: <img src="https://assets.avepoint.com/upload/images/landingpage/pricing/APS-Pricing-icons-users.svg" className="h-4 w-4 md:h-6 md:w-6 text-[#ef483d]" alt="Users" />,
      iconBg: "bg-[#ffebee]",
      blobColor: "bg-[#ef483d]",
    },
    {
      title: "Experience",
      value: "20",
      suffix: "+",
      description: "Years of public sector experience",
      icon: <img src="https://assets.avepoint.com/upload/images/landingpage/pricing/APS-Pricing-icons-experience.svg" className="h-4 w-4 md:h-6 md:w-6 text-[#ef483d]" alt="Experience" />,
      iconBg: "bg-[#fff8e1]",
      blobColor: "bg-[#b99532]",
    },
    {
      title: "Compliance",
      value: "100",
      suffix: "%",
      description: "Compliance with federal mandates",
      icon: <img src="https://assets.avepoint.com/upload/images/landingpage/pricing/APS-Pricing-icons-compliance.svg" className="h-4 w-4 md:h-6 md:w-6 text-[#ef483d]" alt="Compliance" />,
      iconBg: "bg-[#e8eaff]",
      blobColor: "bg-[#3037e2]",
    },
  ]

  // Certification data
  const certifications = [
    {
      name: "FedRAMP Authorized",
      icon: <img src="https://assets.avepoint.com/upload/images/landingpage/pricing/icons-FedRAMP-authorized.svg" width={32} alt="FedRAMP Authorized" />,
    },
    {
      name: "Built to NIST Requirements",
      icon: <img src="https://assets.avepoint.com/upload/images/landingpage/pricing/icons-built-to-NIST-requirements.svg" width={32} alt="Built to NIST Requirements" />,
    },
    {
      name: "Built to CMMC Requirements",
      icon: <img src="https://assets.avepoint.com/upload/images/landingpage/pricing/icons-built-to-CMMC-requirements.svg" width={32} alt="Built to CMMC Requirements" />,
    },
    {
      name: "SOC 2 Certified",
      icon: <img src="https://assets.avepoint.com/upload/images/landingpage/pricing/icons-SOC-2-certified.svg" width={32} alt="SOC 2 Certified" />,
    },
    {
      name: "Built to HIPAA Requirements",
      icon: <img src="https://assets.avepoint.com/upload/images/landingpage/pricing/icons-built-to-HIPAA-requirements.svg" width={32} alt="Built to HIPAA Requirements" />,
    },
  ]

  return (
    <section className="w-full py-16 md:py-24 bg-white">
      <div className="container px-4 md:px-6 mx-auto">
        {/* Header with refined typography */}
        <motion.div
          className="flex flex-col items-center text-center mb-16 md:mb-24"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={containerVariants}
        >
          {/* Removed "Trusted Worldwide" text as requested */}

          <motion.h2
            className="text-3xl md:text-5xl font-bold tracking-tight text-[#1d1d1f] mb-6 max-w-4xl"
            variants={itemVariants}
          >
            Reliable. Experienced. Trusted.
          </motion.h2>
        </motion.div>

        {/* Stats with Apple-style cards - Desktop version */}
        <div ref={statsRef} className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-20">
          {stats.map((stat, index) => (
            <motion.div
              key={`desktop-stat-${index}`}
              className="bg-white rounded-2xl p-4 md:p-8 shadow-sm border border-[#e5e5e7] relative overflow-hidden group"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -5, boxShadow: "0 10px 30px rgba(0,0,0,0.05)" }}
            >
              {/* Decorative gradient blob */}
              <div
                className={`absolute -top-10 -right-10 w-16 h-16 md:w-32 md:h-32 ${stat.blobColor} opacity-5 rounded-full blur-2xl group-hover:opacity-10 transition-opacity duration-500`}
              ></div>

              <div className="relative z-10">
                <div className="flex items-center justify-between mb-3 md:mb-6">
                  <div className={`w-8 h-8 md:w-12 md:h-12 rounded-full ${stat.iconBg} flex items-center justify-center`}>
                    {stat.icon}
                  </div>
                  <div className="text-xs text-[#86868b] font-medium md:uppercase md:tracking-wide">{stat.title}</div>
                </div>

                <div className="flex flex-col">
                  <div className="text-2xl sm:text-4xl md:text-5xl font-bold text-[#1d1d1f] mb-2">
                    <span className="stat-counter" data-target={stat.value} data-suffix={stat.suffix}>
                      0{stat.suffix}
                    </span>
                  </div>
                  <p className="text-xs md:text-base text-[#424245]">{stat.description}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Compliance & Certifications Section */}
        <motion.div
          className="relative"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="text-center mb-10">
            <h3 className="text-2xl md:text-3xl font-semibold text-[#1d1d1f] mb-3 tracking-tight">
              Compliance & Certifications
            </h3>
            <p className="text-[#86868b] text-base md:text-lg max-w-2xl mx-auto font-normal leading-relaxed">
              Our solutions adhere to the highest security standards mandated for public sector organizations.
            </p>
          </div>

          {/* Desktop view - horizontal layout with icons above text */}
          <div className="bg-[#f5f5f7] md:bg-transparent rounded-2xl md:rounded-none overflow-hidden flex flex-wrap justify-center gap-0 md:gap-8">
            {certifications.map((cert, index) => (
              <motion.div
                key={`desktop-cert-${index}`}
                className="flex flex-row md:flex-col items-center w-full md:w-[180px] gap-4 group transition-colors duration-300 hover:bg-white/50 border-b border-[#e5e5e7]/30 md:border-none px-4 py-5 md:p-0"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <div className="w-8 h-8 md:h-20 md:w-20 bg-[#f5f5f7] rounded-full flex items-center justify-center group-hover:scale-110 md:group-hover:scale-100 group-hover:text-[#3037e2] group hover:bg-[#f0f4ff] transition-colors duration-300 shadow-sm">
                  {cert.icon}
                </div>
                <p className="text-base font-medium text-center text-[#1d1d1f]">{cert.name}</p>
              </motion.div>
            ))}
          </div>

        </motion.div>
      </div>
    </section>
  )
}
